import { useState, useEffect, useCallback } from "react";
import { Users, Eye, MousePointerClick } from "lucide-react";
import api from "../lib/api";
import { clsx } from "../lib/format";
import { AreaChart, Area, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";

const RANGES = [["today", "Today"], ["week", "This Week"], ["month", "This Month"], ["year", "This Year"]];

export default function AdminAnalytics() {
  const [rng, setRng] = useState("week");
  const [data, setData] = useState(null);

  const load = useCallback(() => api.get("/admin/analytics", { params: { rng } }).then((r) => setData(r.data)), [rng]);
  useEffect(() => { load(); }, [load]);

  if (!data) return <div className="text-fx-s3">Loading analytics…</div>;

  const cards = [
    { label: "Visitors", value: data.visitors, icon: Users },
    { label: "Page Views", value: data.pageviews, icon: Eye },
    { label: "Sessions", value: data.sessions, icon: MousePointerClick },
    { label: "New Visitors", value: data.new_visitors, icon: Users },
    { label: "Returning", value: data.returning_visitors, icon: Users },
  ];

  const Bars = ({ title, items, testid }) => (
    <div className="rounded-xl bg-fx-s1 border border-white/5 p-5" data-testid={testid}>
      <h3 className="font-display font-bold mb-3">{title}</h3>
      {items.length === 0 && <p className="text-fx-s3 text-sm">No data yet.</p>}
      {items.map((it) => {
        const max = Math.max(...items.map((x) => x.count), 1);
        return (
          <div key={it.name} className="mb-2">
            <div className="flex justify-between text-xs mb-1"><span className="text-fx-s3 truncate max-w-[220px]">{it.name}</span><span className="font-mono">{it.count}</span></div>
            <div className="h-1.5 rounded-full bg-fx-base overflow-hidden"><div className="h-full bg-brandblue" style={{ width: `${(it.count / max) * 100}%` }} /></div>
          </div>
        );
      })}
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex gap-2 flex-wrap">
        {RANGES.map(([v, l]) => (
          <button key={v} onClick={() => setRng(v)} data-testid={`analytics-range-${v}`}
            className={clsx("px-3 py-1.5 rounded-lg text-sm font-medium border", rng === v ? "bg-brandblue text-white border-brandblue" : "bg-fx-s1 text-fx-s3 border-white/5 hover:text-white")}>{l}</button>
        ))}
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        {cards.map((c) => (
          <div key={c.label} data-testid={`analytics-card-${c.label.toLowerCase().replace(/\s+/g, "-")}`} className="rounded-xl bg-fx-s1 border border-white/5 p-4">
            <div className="flex items-center gap-2 text-fx-s3 text-xs"><c.icon className="h-3.5 w-3.5" /> {c.label}</div>
            <div className="font-mono text-2xl font-bold mt-1">{c.value}</div>
          </div>
        ))}
      </div>
      <div className="rounded-xl bg-fx-s1 border border-white/5 p-5">
        <h2 className="font-display font-bold mb-4">Traffic Over Time</h2>
        <ResponsiveContainer width="100%" height={220}>
          <AreaChart data={data.timeseries}>
            <defs><linearGradient id="traf" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#3B82F6" stopOpacity={0.5} /><stop offset="100%" stopColor="#3B82F6" stopOpacity={0} /></linearGradient></defs>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
            <XAxis dataKey="day" stroke="#64748B" fontSize={11} /><YAxis stroke="#64748B" fontSize={11} />
            <Tooltip contentStyle={{ background: "#1C1F26", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8 }} />
            <Area type="monotone" dataKey="views" stroke="#3B82F6" fill="url(#traf)" name="Page views" />
          </AreaChart>
        </ResponsiveContainer>
      </div>
      <div className="grid md:grid-cols-3 gap-4">
        <Bars title="Top Pages" items={data.top_pages} testid="analytics-top-pages" />
        <Bars title="Devices" items={data.devices} testid="analytics-devices" />
        <Bars title="Browsers" items={data.browsers} testid="analytics-browsers" />
      </div>
    </div>
  );
}
